/**
 * Trial Top Bar
 * Common header component showing trial ID, version, and back navigation
 * Used in both PRS Analysis and Protocol Generation Wizard views
 */

import React from 'react';
import {Box, Typography, IconButton, Chip, Tooltip} from '@mui/material';
import {ArrowBack as BackIcon, AutoFixHigh as WizardIcon, Analytics as AnalysisIcon} from '@mui/icons-material';

interface TrialTopBarProps {
  trialId: string;
  version: string;
  sessionName?: string;
  platform?: string;
  onBack: () => void;
  viewType?: 'analysis' | 'wizard';
  /** Optional actions (e.g. Commit/Discard buttons) rendered on the right side */
  actions?: React.ReactNode;
}

export const TrialTopBar: React.FC<TrialTopBarProps> = ({
  trialId,
  version,
  sessionName,
  platform,
  onBack,
  viewType = 'analysis',
  actions,
}) => {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        mb: 1.5,
      }}
    >
      {/* Left: Back Icon + Trial Info (two lines) */}
      <Box sx={{display: 'flex', alignItems: 'flex-start', gap: 1}}>
        <Tooltip title="Back to Trials">
          <IconButton
            onClick={onBack}
            size="small"
            sx={{mt: 0.25, color: 'text.primary'}}
          >
            <BackIcon />
          </IconButton>
        </Tooltip>

        <Box>
          <Typography variant="h6" component="h1" sx={{fontWeight: 600, lineHeight: 1.3}}>
            {trialId}
          </Typography>
          <Box sx={{display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.5}}>
            <Chip
              label={sessionName || version}
              color="primary"
              size="small"
              sx={{fontWeight: 500, height: 22, fontSize: '0.75rem'}}
            />
            {platform && (
              <Chip
                label={platform.toUpperCase()}
                color="success"
                size="small"
                sx={{fontWeight: 600, height: 22, fontSize: '0.7rem'}}
              />
            )}
          </Box>
        </Box>
      </Box>

      {/* Right: View type indicator + optional actions */}
      <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5}}>
        <Chip
          icon={viewType === 'analysis' ? <AnalysisIcon sx={{fontSize: 16}} /> : <WizardIcon sx={{fontSize: 16}} />}
          label={viewType === 'analysis' ? 'Analysis Mode' : 'Wizard Mode'}
          size="medium"
          sx={{
            fontWeight: 400,
            fontSize: '0.8rem',
            bgcolor: '#f3e5f5',
            color: '#7b1fa2',
            border: '1.5px solid #ce93d8',
            '& .MuiChip-icon': {
              color: '#7b1fa2',
            },
          }}
        />
        {actions}
      </Box>
    </Box>
  );
};
